import { useEffect, useState } from "react";
import { Eraser, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api from "@/lib/api";

const LABELS = { giocate: "Giocate", messaggi_whatsapp: "Messaggi WhatsApp", otp: "OTP di ritiro", campagne: "Campagne", codici_sconto: "Codici sconto", lotti: "Lotti codici", codici: "Codici", avvisi_sede: "Avvisi sede" };

function CleanupPreview({ prev, active, name, setName, run, busy }) {
  return (
    <div className="space-y-3">
      {active && <p className="text-xs rounded-md bg-red-500/10 border border-red-500/30 p-2 text-red-300" data-testid="cleanup-active-warning">Il concorso è <b>{prev.status}</b>: la pulizia eliminerà dati reali. Ti verrà chiesta una seconda conferma.</p>}
      <ul className="grid grid-cols-2 gap-1.5 text-sm" data-testid="cleanup-counts">
        {Object.entries(prev.counts).map(([k, v]) => (
          <li key={k} className="flex justify-between rounded-md bg-secondary/40 px-3 py-1.5"><span className="text-muted-foreground">{LABELS[k] || k}</span><b>{v}</b></li>
        ))}
      </ul>
      <p className="text-xs text-muted-foreground">Verranno eliminati giocate, ricevute, OTP, messaggi e campagne WhatsApp, codici sconto, avvisi sede e tutti i lotti codici del concorso. Premi, sedi e configurazione restano.</p>
      <div className="space-y-1.5">
        <Label className="text-xs">Per confermare digita il nome del concorso: <b>{prev.contest_name}</b></Label>
        <Input data-testid="cleanup-confirm-input" value={name} onChange={(e) => setName(e.target.value)} placeholder={prev.contest_name} />
      </div>
      <Button data-testid="cleanup-confirm-button" onClick={run} disabled={busy || name.trim() !== prev.contest_name} className="w-full bg-red-600 hover:bg-red-700">
        {busy ? "Pulizia in corso…" : "Elimina definitivamente"}
      </Button>
    </div>
  );
}

export default function CleanupDialog({ contestId, onDone, renderTrigger, open: openProp, onOpenChange }) {
  const [openState, setOpenState] = useState(false);
  const open = openProp ?? openState;
  const setOpen = onOpenChange ?? setOpenState;
  const [prev, setPrev] = useState(null);
  const [name, setName] = useState("");
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) { setName(""); setAck(false); api.cleanupPreview(contestId).then(setPrev).catch(() => toast.error("Anteprima non disponibile")); }
  }, [open, contestId]);
  const active = prev && prev.status !== "Programmato";
  const run = async () => {
    if (active && !window.confirm("ATTENZIONE: il concorso è già iniziato. Tutte le giocate e i codici verranno eliminati definitivamente. Continuare?")) return;
    setBusy(true);
    try {
      const r = await api.cleanupContest(contestId, { confirm_name: name, acknowledge_active: active });
      toast.success(`Pulizia completata: ${r.deleted.giocate} giocate e ${r.deleted.codici} codici eliminati`);
      setOpen(false);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Pulizia non riuscita");
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      {renderTrigger ? renderTrigger(() => setOpen(true)) : <Button data-testid="cleanup-open-button" variant="outline" className="gap-2 border-red-500/40 text-red-400 hover:bg-red-500/10" onClick={() => setOpen(true)}>
        <Eraser className="h-4 w-4" /> Pulizia dati di prova
      </Button>}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="cleanup-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-red-400" /> Pulizia dati di prova</DialogTitle></DialogHeader>
          {!prev ? <p className="text-sm text-muted-foreground">Caricamento…</p> : <CleanupPreview prev={prev} active={active} name={name} setName={setName} run={run} busy={busy} />}
        </DialogContent>
      </Dialog>
    </>
  );
}
